import React, { useState, useEffect } from 'react';
import { Mail, ExternalLink, Inbox, Send, RefreshCw, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminWebmail = () => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [sendForm, setSendForm] = useState({ to: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (activeTab === 'inbox') {
      fetchInbox();
    }
  }, [activeTab]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchInbox = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/webmail/inbox`, { headers: getAuthHeader() });
      setEmails(res.data.emails || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to fetch emails';
      toast({ title: "Email Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/webmail/send`, sendForm, { headers: getAuthHeader() });
      toast({ title: "Email Sent", description: `Email sent to ${sendForm.to}` });
      setSendForm({ to: '', subject: '', body: '' });
      setActiveTab('inbox');
      fetchInbox();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send email';
      toast({ title: "Send Failed", description: msg, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 sm:p-8" data-testid="admin-webmail-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-800">Webmail</h1>
          <p className="text-slate-600 mt-1 text-sm">info@uskesupplyllc.com</p>
        </div>
        <div className="flex gap-2">
          <a
            href="https://privateemail.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" size="sm" className="text-sm" data-testid="namecheap-webmail-link">
              <ExternalLink className="h-4 w-4 mr-1" />
              Namecheap Portal
            </Button>
          </a>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('inbox')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'inbox'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="inbox-tab"
        >
          <Inbox className="h-4 w-4 inline mr-1" />
          Inbox
        </button>
        <button
          onClick={() => setActiveTab('compose')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'compose'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="compose-tab"
        >
          <Send className="h-4 w-4 inline mr-1" />
          Compose
        </button>
      </div>

      {/* Inbox Tab */}
      {activeTab === 'inbox' && (
        <div>
          <div className="flex justify-end mb-4">
            <Button variant="outline" size="sm" onClick={fetchInbox} disabled={loading} data-testid="refresh-inbox-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : emails.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="p-8 text-center">
                <Mail className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No emails found. Your inbox is empty or there was a connection issue.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {emails.map((email) => (
                <Card
                  key={email.id}
                  className={`border cursor-pointer transition-all hover:shadow-md ${
                    selectedEmail?.id === email.id ? 'border-purple-500 bg-purple-50' : 'hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedEmail(selectedEmail?.id === email.id ? null : email)}
                  data-testid={`email-item-${email.id}`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-800 text-sm truncate">{email.from}</p>
                        <p className="text-sm text-slate-700 font-medium truncate">{email.subject || '(no subject)'}</p>
                        <p className="text-xs text-slate-400 mt-1">{email.date}</p>
                      </div>
                    </div>
                    {selectedEmail?.id === email.id && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-slate-500 mb-1">To: {email.to}</p>
                        <div className="bg-white p-3 rounded border text-sm text-slate-700 whitespace-pre-wrap max-h-60 overflow-y-auto">
                          {email.body_preview || '(empty)'}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSendForm({
                              to: email.from.match(/<(.+)>/)?.[1] || email.from,
                              subject: `Re: ${email.subject}`,
                              body: `\n\n--- Original Message ---\nFrom: ${email.from}\nDate: ${email.date}\n\n${email.body_preview}`
                            });
                            setActiveTab('compose');
                          }}
                        >
                          <Send className="h-3 w-3 mr-1" /> Reply
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compose Tab */}
      {activeTab === 'compose' && (
        <Card className="border-2" data-testid="compose-form">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Send className="h-5 w-5 text-purple-600" />
              New Email
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <Label htmlFor="email-to">To</Label>
                <Input
                  id="email-to"
                  type="email"
                  placeholder="recipient@example.com"
                  value={sendForm.to}
                  onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                  required
                  disabled={sending}
                  data-testid="compose-to-input"
                />
              </div>
              <div>
                <Label htmlFor="email-subject">Subject</Label>
                <Input
                  id="email-subject"
                  placeholder="Email subject"
                  value={sendForm.subject}
                  onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
                  required
                  disabled={sending}
                  data-testid="compose-subject-input"
                />
              </div>
              <div>
                <Label htmlFor="email-body">Message</Label>
                <Textarea
                  id="email-body"
                  rows={8}
                  placeholder="Type your message..."
                  value={sendForm.body}
                  onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })}
                  required
                  disabled={sending}
                  data-testid="compose-body-input"
                />
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-slate-400">Sending as info@uskesupplyllc.com</p>
                <Button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={sending}
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminWebmail;
